import { createRouter as createTanStackRouter } from '@tanstack/react-router';
import { setupRouterSsrQueryIntegration } from '@tanstack/react-router-ssr-query';

import { createQueryClient } from '#features/api/utils/queryClient';
import { DefaultCatchBoundary } from '#features/error/components/DefaultCatchBoundary';
import { dynamicActivate } from '#features/i18n';
import { i18n } from '#features/i18n/i18n';
import { getLocaleFromRequest } from '#features/i18n/server';
import { setupLinguiSsrIntegration } from '#features/i18n/utils/setupLinguiSsrIntegration';
import type { AuthStatus } from '#modules/user/features/auth/utils/checkIsAuth';
import { envConfig } from '#constants/envConfig';
import { routeTree } from './routeTree.gen';

/**
 * TanStack Start resolves `src/router.tsx` as its router entry and calls `getRouter()` itself.
 *
 * <p>On the server it calls it **per request**, and the router carries request-scoped state — the
 * active i18n locale above all. Memoising it there pins whatever locale the first request happened
 * to resolve onto every later one, so /ru bounces back to / on an English-warmed process.
 *
 * <p>On the client there is exactly one router for the page's lifetime, and our client entry needs
 * that very instance for Sentry tracing — a second one would be a router nobody navigates. So the
 * promise is memoised on the client only.
 */
let clientRouterPromise: ReturnType<typeof createRouter> | undefined;

export function getRouter() {
  if (envConfig.IN_SERVER) {
    return createRouter();
  }
  clientRouterPromise ??= createRouter();
  return clientRouterPromise;
}

/**
 * Initial auth context for the app.
 *
 * The web build starts as a guest and lets the server decide per request. The app has no such
 * server: it boots from a shell prerendered at build time, where nobody was signed in, so
 * starting as a guest means the first render is wrong for everyone who is logged in — the auth
 * guard bounces them to /sign-in, and fixing it afterwards makes the whole navigation flicker.
 * Reading the stored token here costs one await before the first render and gets it right the
 * first time.
 */
async function initialAuthContext(): Promise<{ isAuth: boolean; authStatus: AuthStatus }> {
  if (!envConfig.IS_NATIVE || envConfig.IN_SERVER) {
    return { isAuth: false, authStatus: 'pending' };
  }
  try {
    const [{ getNativeToken }, { isLikelyJwtToken }] = await Promise.all([
      import('#features/api/utils/nativeAuth'),
      import('#modules/user/features/auth/utils/isLikelyJwtToken'),
    ]);
    return { isAuth: isLikelyJwtToken(await getNativeToken()), authStatus: 'ok' };
  } catch {
    // Couldn't read storage — say "don't know" rather than "signed out", so the guard
    // doesn't throw a valid session out on a transient failure.
    return { isAuth: false, authStatus: 'pending' };
  }
}

async function createRouter() {
  const queryClient = createQueryClient();

  if (envConfig.IN_SERVER) {
    await dynamicActivate(i18n, getLocaleFromRequest());
  }

  const { isAuth, authStatus } = await initialAuthContext();

  const router = createTanStackRouter({
    routeTree,
    scrollRestoration: true,
    //defaultPreload: 'intent',
    scrollRestorationBehavior: 'auto',
    // Since we're using React Query, we don't want loader calls to ever be stale
    // This will ensure that the loader is always called when the route is preloaded or visited
    defaultPreloadStaleTime: 0,
    // routeMasks: [dashboardPostToPostMask],
    context: { queryClient, i18n, isAuth, authStatus },
    // defaultPreload: 'intent',
    defaultErrorComponent: DefaultCatchBoundary,
    defaultStructuralSharing: true,
  });

  setupRouterSsrQueryIntegration({
    router,
    queryClient,
  });

  setupLinguiSsrIntegration({
    router,
    i18n,
    wrapQueryClient: true,
  });

  return router;
}

declare module '@tanstack/react-router' {
  interface Register {
    router: Awaited<ReturnType<typeof getRouter>>;
  }

  // Routes whose component renders its own footer (e.g. the landing) set this so
  // MainLayout suppresses the global footer instead of double-rendering it.
  // `rendersOwnHeader` does the same for the global site header: product surfaces
  // that ship their own chrome (the embedded LFG bot landing, the Discord admin)
  // set it so MainLayout doesn't stack the teamplay.gg header on top of theirs.
  interface StaticDataRouteOption {
    rendersOwnFooter?: boolean;
    rendersOwnHeader?: boolean;
  }
}
